import React, { useState, useEffect, useRef } from "react";
import { BiPlus } from "react-icons/bi";
import { FaUsers } from "react-icons/fa";
import { FiCopy, FiDownload, FiXCircle } from "react-icons/fi";

import { toast } from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useComponentTranslation } from "@/services/i18n";
import Modal from "@/components/modal";

import api from "@/services/api";
import useStore from "@/services/store";

const translations = {
  en: {
    fetch_error: "Failed to fetch job ads",
    fetch_status_error: "Failed to fetch job status counts",
    search_placeholder: "Search job ads...",
    status_all: "All",
    status_live: "Live",
    status_draft: "Draft",
    status_closed: "Closed",
    empty_title: "No job ads found",
    empty_description: "Get started by creating a new job ad",
    create_button: "Create Job Ad",
    candidates_count: "{{count}} candidates",
    title_label: "Title",
    brief_label: "Brief",
    brief_placeholder: "Describe the role, key responsibilities, requirements...",
    generating_description: "Generating description...",
    candidates_label: "Candidates",
    closed_on_label: "Closed on",
    jobs_count: "{{count}} jobs",
    end_of_list: "End of list",
  },
  fr: {
    fetch_error: "Échec de la récupération des offres d'emploi",
    fetch_status_error: "Échec de la récupération des compteurs de statut",
    search_placeholder: "Rechercher des annonces...",
    status_all: "Tous",
    status_live: "Live",
    status_draft: "Brouillon",
    status_closed: "Closed",
    empty_title: "Aucune offre d'emploi trouvée",
    empty_description: "Commencez par créer une nouvelle offre d'emploi",
    create_button: "Créer une offre d'emploi",
    candidates_count: "{{count}} candidats",
    title_label: "Titre",
    brief_label: "Brief",
    brief_placeholder: "Décrivez le poste, les missions principales, les prérequis...",
    generating_description: "Génération de la description...",
    candidates_label: "Candidats",
    closed_on_label: "Fermé le",
    jobs_count: "{{count}} offres",
    end_of_list: "Fin de la liste",
  },
};

const FILTERS = [
  { key: "ALL", label: "status_all" },
  { key: "OPEN", label: "status_live" },
  { key: "DRAFT", label: "status_draft" },
  { key: "CLOSED", label: "status_closed" },
];

const CLOSED_STATUSES = ["CLOSED", "EXPIRED", "DISABLED"];
const CANDIDATE_STATUSES = ["APPLIED", "REFUSED", "PRESELECTED", "INTERVIEW", "SELECTED"];
const LIMIT = 50;

const statusForFilter = (filter) => {
  if (filter === "ALL") return "ALL";
  if (filter === "CLOSED") return CLOSED_STATUSES;
  return [filter];
};

function getStatusBadge(status) {
  if (status === "OPEN") return { label: "Live", cls: "bg-emerald-50 text-emerald-700" };
  if (status === "DRAFT") return { label: "Draft", cls: "bg-gray-100 text-gray-600" };
  return { label: "Closed", cls: "bg-gray-100 text-gray-600" };
}

export default function List() {
  const { t } = useComponentTranslation(translations);
  const { user, organization } = useStore();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeFilter, setActiveFilter] = useState(searchParams.get("filter") || "OPEN");
  const [jobs, setJobs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [statusCounts, setStatusCounts] = useState([]);
  const [candidatesCounts, setCandidatesCounts] = useState([]);
  const [recentCounts, setRecentCounts] = useState([]);
  const [jobPosts, setJobPosts] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const sentinelRef = useRef(null);

  // Enrich only the newly fetched batch and merge into the id-keyed maps,
  // so scrolling does not re-aggregate the whole accumulated list.
  const enrichBatch = async (batch) => {
    if (!batch?.length) return;
    const ids = batch.map((job) => job._id);

    try {
      const { ok, data } = await api.post("/application/aggregate", { job_id: ids, status: CANDIDATE_STATUSES });
      if (ok) setCandidatesCounts((prev) => [...prev, ...(data.job_id || [])]);
    } catch (e) {
      console.error(e);
    }

    try {
      const since = new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString();
      const { ok, data } = await api.post("/application/aggregate", { job_id: ids, status: CANDIDATE_STATUSES, created_after: since });
      if (ok) setRecentCounts((prev) => [...prev, ...(data.job_id || [])]);
    } catch (e) {
      console.error(e);
    }

    try {
      const results = await Promise.all(ids.map((id) => api.post("/post/search", { job_id: id, status: "active", limit: 100 })));
      setJobPosts((prev) => {
        const map = { ...prev };
        ids.forEach((id, index) => {
          if (results[index]?.ok) map[id] = results[index].data || [];
        });
        return map;
      });
    } catch (e) {
      console.error("Error fetching posts:", e);
    }
  };

  const fetchJobs = async (pageToFetch, { append }) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const params = {
        page: pageToFetch,
        limit: LIMIT,
        organization_id: organization?._id,
        organization_name: organization?.name,
        status: statusForFilter(activeFilter),
      };
      const { ok, data, total } = await api.post("/job/search", params);
      if (!ok) return;
      const batch = data || [];
      setTotal(total);
      setHasMore(pageToFetch * LIMIT < total);
      setJobs((prev) => (append && prev ? [...prev, ...batch] : batch));
      enrichBatch(batch);
    } catch (e) {
      toast.error(e.code);
    } finally {
      if (append) setLoadingMore(false);
      else setLoading(false);
    }
  };

  const fetchStatusCounts = async () => {
    try {
      const { ok, data } = await api.post("/job/aggregate", { organization_id: organization?._id });
      if (ok) setStatusCounts(data);
    } catch (e) {
      toast.error(e.code);
    }
  };

  // Reset and load the first batch whenever the filter or organization changes.
  useEffect(() => {
    setJobs(null);
    setPage(1);
    setHasMore(false);
    setCandidatesCounts([]);
    setRecentCounts([]);
    setJobPosts({});
    setSelectedIds(new Set());
    setSearchParams({ filter: activeFilter });
    fetchJobs(1, { append: false });
    fetchStatusCounts();
  }, [activeFilter, organization?._id]);

  // Infinite scroll: load the next batch when the sentinel comes into view.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          const next = page + 1;
          setPage(next);
          fetchJobs(next, { append: true });
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, page, activeFilter]);

  const reload = () => {
    setPage(1);
    setCandidatesCounts([]);
    setRecentCounts([]);
    setJobPosts({});
    fetchJobs(1, { append: false });
    fetchStatusCounts();
  };

  const handleToggleJob = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const handleSelectAll = () => {
    if (!jobs) return;
    if (selectedIds.size === jobs.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(jobs.map((j) => j._id)));
  };

  const handleClearSelection = () => setSelectedIds(new Set());

  const handleDuplicate = async () => {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    try {
      const ids = Array.from(selectedIds);
      const results = await Promise.all(
        ids.map((id) =>
          api.post(`/job/${id}/duplicate`, {
            organization_id: organization?._id,
            organization_name: organization?.name,
          }),
        ),
      );
      const successCount = results.filter((r) => r?.ok).length;
      const failedCount = ids.length - successCount;
      if (successCount > 0) toast.success(`${successCount} job${successCount > 1 ? "s" : ""} duplicated`);
      if (failedCount > 0) toast.error(`${failedCount} failed`);
      handleClearSelection();
      reload();
    } catch (e) {
      toast.error("Duplication failed");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleClose = async () => {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    try {
      const ids = Array.from(selectedIds);
      const results = await Promise.all(ids.map((id) => api.put(`/job/${id}`, { status: "CLOSED" })));
      const successCount = results.filter((r) => r?.ok).length;
      const failedCount = ids.length - successCount;
      if (successCount > 0) toast.success(`${successCount} job${successCount > 1 ? "s" : ""} closed`);
      if (failedCount > 0) toast.error(`${failedCount} failed`);
      handleClearSelection();
      reload();
    } catch (e) {
      toast.error("Close failed");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleExport = () => {
    if (!jobs || selectedIds.size === 0) return;
    const selectedJobs = jobs.filter((j) => selectedIds.has(j._id));
    const rows = selectedJobs.map((j) => {
      const candidates = candidatesCounts?.find((c) => c._id === j._id)?.count || 0;
      return [
        j.index ?? "",
        `"${(j.title || "").replace(/"/g, '""')}"`,
        j.status || "",
        candidates,
        j.created_at ? new Date(j.created_at).toISOString() : "",
        j.updated_at ? new Date(j.updated_at).toISOString() : "",
      ];
    });
    const csv = [["#", "Title", "Status", "Candidates", "Created", "Updated"], ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `jobs_${activeFilter.toLowerCase()}_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Export ready");
  };

  const getTabCount = (key) => {
    if (!statusCounts) return 0;
    if (key === "CLOSED") {
      return statusCounts.filter((item) => CLOSED_STATUSES.includes(item._id)).reduce((sum, item) => sum + item.count, 0);
    }
    const found = statusCounts.find((item) => item._id === key);
    return found ? found.count : 0;
  };

  return (
    <div className="w-full p-6">
      {/* Filter pills + Create Button */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex gap-2 flex-wrap">
          {FILTERS.map((f) => {
            const count = getTabCount(f.key);
            const isActive = activeFilter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setActiveFilter(f.key)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors flex items-center gap-2 ${
                  isActive ? "bg-[#6C4CFF] border-[#6C4CFF] text-white" : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                }`}
              >
                {t(f.label)}
                <span className={`text-xs px-1.5 py-0.5 rounded-full min-w-[20px] text-center ${isActive ? "bg-white/20 text-white" : "bg-gray-100 text-gray-400"}`}>{count}</span>
              </button>
            );
          })}
        </div>
        <CreateJob />
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="mb-3 flex items-center justify-between bg-[#6C4CFF] rounded-xl py-2.5 px-4">
          <div className="flex items-center gap-3">
            <span className="text-white font-semibold text-sm">{selectedIds.size} selected</span>
            <button
              onClick={handleDuplicate}
              disabled={bulkLoading}
              className="bg-white/10 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-white/20 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <FiCopy className="w-3.5 h-3.5" />
              Duplicate
            </button>
            <button
              onClick={handleExport}
              disabled={bulkLoading}
              className="bg-white text-gray-900 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <FiDownload className="w-3.5 h-3.5" />
              Export CSV
            </button>
            <button
              onClick={handleClose}
              disabled={bulkLoading}
              className="bg-white/10 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-white/20 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <FiXCircle className="w-3.5 h-3.5" />
              Close
            </button>
          </div>
          <button onClick={handleClearSelection} className="text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-white/10 transition-colors">
            Clear
          </button>
        </div>
      )}

      {/* Job Ads Table */}
      {loading ? (
        <LoadingState />
      ) : jobs?.length === 0 ? (
        <EmptyState />
      ) : (
        <JobTable
          jobs={jobs}
          candidatesCounts={candidatesCounts}
          recentCounts={recentCounts}
          jobPosts={jobPosts}
          selectedIds={selectedIds}
          onToggle={handleToggleJob}
          onToggleAll={handleSelectAll}
        />
      )}

      {/* Infinite scroll sentinel */}
      {jobs && jobs.length > 0 && (
        <div ref={sentinelRef} className="py-6 text-center text-sm text-gray-400">
          {loadingMore ? (
            <span className="inline-block h-4 w-4 border-2 border-gray-300 border-t-[#6C4CFF] rounded-full animate-spin" />
          ) : !hasMore ? (
            t("end_of_list")
          ) : null}
        </div>
      )}
    </div>
  );
}

function daysAgo(date) {
  const diff = Math.floor((Date.now() - new Date(date).getTime()) / (1000 * 60 * 60 * 24));
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return `${diff}d ago`;
}

const JobTable = ({ jobs, candidatesCounts, recentCounts, jobPosts, selectedIds, onToggle, onToggleAll }) => {
  const { t } = useComponentTranslation(translations);
  const navigate = useNavigate();
  const allSelected = jobs.length > 0 && jobs.every((j) => selectedIds?.has(j._id));

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50">
            <th className="px-6 py-3 w-12">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={onToggleAll}
                className="w-4 h-4 rounded border-gray-300 text-[#6C4CFF] focus:ring-[#6C4CFF] cursor-pointer"
              />
            </th>
            <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">#</th>
            <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">{t("title_label")}</th>
            <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Status</th>
            <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">{t("candidates_label")}</th>
            <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Last 48h</th>
            <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Channels</th>
            <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Created</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {jobs.map((job) => {
            const candidatesCount = candidatesCounts?.find((item) => item._id === job._id)?.count || 0;
            const recentCount = recentCounts?.find((item) => item._id === job._id)?.count || 0;
            const posts = jobPosts[job._id] || [];
            const isSelected = selectedIds?.has(job._id);
            const isClosedJob = CLOSED_STATUSES.includes(job.status);
            const badge = getStatusBadge(job.status);
            return (
              <tr
                key={job._id}
                onClick={() => navigate(`/recruiter/job-ads/${job._id}`)}
                className={`cursor-pointer transition-colors ${isSelected ? "bg-purple-50" : "hover:bg-gray-50"}`}
              >
                <td className="px-6 py-4 w-12" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggle(job._id)}
                    className="w-4 h-4 rounded border-gray-300 text-[#6C4CFF] focus:ring-[#6C4CFF] cursor-pointer"
                  />
                </td>
                <td className="px-6 py-4 text-sm text-gray-400 whitespace-nowrap">{job.index}</td>
                <td className={`px-6 py-4 text-sm font-semibold ${isClosedJob ? "text-gray-400 line-through" : "text-gray-900"}`}>{job.title}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${badge.cls}`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-current" />
                    {badge.label}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">{candidatesCount}</td>
                <td className="px-6 py-4 text-sm whitespace-nowrap">
                  {recentCount > 0 ? <span className="text-green-600 font-medium">+{recentCount}</span> : <span className="text-gray-400">—</span>}
                </td>
                <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">{posts.length || "—"}</td>
                <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">{daysAgo(job.created_at)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const LoadingState = () => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50">
            <th className="px-6 py-3 w-12"></th>
            <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">#</th>
            <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Title</th>
            <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Status</th>
            <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Candidates</th>
            <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Last 48h</th>
            <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Channels</th>
            <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Created</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {Array.from({ length: 5 }, (_, index) => (
            <tr key={index}>
              <td className="px-6 py-4 w-12">
                <div className="h-4 w-4 bg-gray-200 rounded animate-pulse" />
              </td>
              <td className="px-6 py-4">
                <div className="h-4 bg-gray-200 rounded w-8 animate-pulse" />
              </td>
              <td className="px-6 py-4">
                <div className="h-4 bg-gray-200 rounded w-64 animate-pulse" />
              </td>
              <td className="px-6 py-4">
                <div className="h-4 bg-gray-200 rounded w-16 animate-pulse" />
              </td>
              <td className="px-6 py-4">
                <div className="h-4 bg-gray-200 rounded w-12 animate-pulse" />
              </td>
              <td className="px-6 py-4">
                <div className="h-4 bg-gray-200 rounded w-10 animate-pulse" />
              </td>
              <td className="px-6 py-4">
                <div className="h-4 bg-gray-200 rounded w-12 animate-pulse" />
              </td>
              <td className="px-6 py-4">
                <div className="h-4 bg-gray-200 rounded w-20 animate-pulse" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const EmptyState = () => {
  const { t } = useComponentTranslation(translations);
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
      <h3 className="text-lg font-semibold text-primary mb-2">{t("empty_title")}</h3>
      <p className="text-sm text-gray-500">{t("empty_description")}</p>
    </div>
  );
};

const CreateJob = () => {
  const { t } = useComponentTranslation(translations);
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [brief, setBrief] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [generating, setGenerating] = useState(false);

  const { organization } = useStore();

  const handleCreateJob = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const { ok, data } = await api.post("/job", { title, brief, organization_id: organization?._id, organization_name: organization?.name });
      if (!ok) {
        toast.error("Failed to create job");
        setSubmitting(false);
        return;
      }
      const jobId = data._id;
      const trimmedBrief = (brief || "").trim();
      if (trimmedBrief.length >= 20) {
        setGenerating(true);
        try {
          await api.post(`/job/${jobId}/generate-description`, { prompt: trimmedBrief });
        } catch (err) {
          console.error("generate-description failed", err);
          toast.error("Description generation failed");
        }
      }
      setIsModalOpen(false);
      toast.success(t("create_button"));
      navigate(`/recruiter/job-ads/${jobId}/ad/job`);
    } catch (err) {
      console.error(err);
      toast.error(err?.code || "Error");
    } finally {
      setSubmitting(false);
      setGenerating(false);
    }
  };

  return (
    <>
      <button type="button" onClick={() => setIsModalOpen(true)} className="btn btn-primary">
        <BiPlus className="w-4 h-4" />
        <span>{t("create_button")}</span>
      </button>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} className="w-1/2">
        <div className="p-12">
          <h2 className="text-xl font-semibold mb-4">{t("create_button")}</h2>
          <form onSubmit={handleCreateJob}>
            <div className="space-y-4">
              <div>
                <label htmlFor="jobTitle" className="input-label">
                  {t("title_label")} <span className="text-red-500">*</span>
                </label>
                <input type="text" id="jobTitle" name="title" value={title || ""} onChange={(e) => setTitle(e.target.value)} className="input" required />
              </div>
              <div>
                <label htmlFor="jobBrief" className="input-label">
                  {t("brief_label")}
                </label>
                <textarea
                  id="jobBrief"
                  name="brief"
                  value={brief || ""}
                  onChange={(e) => setBrief(e.target.value)}
                  rows={6}
                  placeholder={t("brief_placeholder")}
                  className="input"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button disabled={!title || submitting} type="submit" className="btn btn-primary">
                {generating ? t("generating_description") : t("create_button")}
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
};
